// src/pages/GestionCajasAdmin.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 💼 GESTIÓN DE CAJAS - VISTA ADMINISTRADOR
// Panel de control para supervisar todas las cajas del sistema
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

const GestionCajasAdmin = () => {
  // Estados
  const [cajasAbiertas, setCajasAbiertas] = useState([]);
  const [cajasCerradas, setCajasCerradas] = useState([]);
  const [cajasFisicas, setCajasFisicas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaActiva, setVistaActiva] = useState("abiertas"); // abiertas, cerradas, fisicas
  const [filtroFecha, setFiltroFecha] = useState("");
  const [filtroCajero, setFiltroCajero] = useState("");
  const [modalDetalle, setModalDetalle] = useState(false);
  const [cajaSeleccionada, setCajaSeleccionada] = useState(null);
  const [resumenGeneral, setResumenGeneral] = useState({
    totalCajasAbiertas: 0,
    totalEfectivo: 0,
    totalTarjeta: 0,
    totalTransferencia: 0,
    totalVentas: 0,
    numVentas: 0
  });

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR DATOS
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    cargarDatos();
    // Actualizar cada 30 segundos
    const interval = setInterval(cargarDatos, 30000);
    return () => clearInterval(interval);
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [cajasRes, fisicasRes] = await Promise.all([
        api.get("/cajas/todas"),
        api.get("/cajas-fisicas")
      ]);

      const todasLasCajas = cajasRes.data || [];
      const abiertas = todasLasCajas.filter(c => c.estado === "abierta");
      const cerradas = todasLasCajas.filter(c => c.estado === "cerrada");

      setCajasAbiertas(abiertas);
      setCajasCerradas(cerradas);
      setCajasFisicas(fisicasRes.data || []);

      // Calcular resumen
      const resumen = abiertas.reduce((acc, caja) => {
        acc.totalEfectivo += parseFloat(caja.total_efectivo || 0);
        acc.totalTarjeta += parseFloat(caja.total_tarjeta || 0);
        acc.totalTransferencia += parseFloat(caja.total_transferencia || 0);
        acc.totalVentas += parseFloat(caja.total_ventas || 0);
        acc.numVentas += parseInt(caja.num_ventas || 0);
        return acc;
      }, {
        totalCajasAbiertas: abiertas.length,
        totalEfectivo: 0,
        totalTarjeta: 0,
        totalTransferencia: 0,
        totalVentas: 0,
        numVentas: 0
      });

      setResumenGeneral(resumen);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔒 FORZAR CIERRE DE CAJA
  // ═══════════════════════════════════════════════════════════════════════════
  const forzarCierreCaja = async (caja) => {
    const { value: formValues } = await Swal.fire({
      title: "⚠️ Forzar Cierre de Caja",
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p><strong>Caja:</strong> ${caja.caja_fisica_numero || caja.numero_caja}</p>
          <p><strong>Cajero:</strong> ${caja.usuario_apertura_nombre}</p>
          <p><strong>Total Ventas:</strong> RD$${parseFloat(caja.total_ventas || 0).toFixed(2)}</p>
        </div>
        <div style="margin-bottom: 10px;">
          <label style="display: block; margin-bottom: 5px; font-weight: 600;">Monto Real en Caja:</label>
          <input id="monto-real" type="number" step="0.01" class="swal2-input" placeholder="0.00" style="width: 100%;">
        </div>
        <div>
          <label style="display: block; margin-bottom: 5px; font-weight: 600;">Motivo del Cierre Forzado:</label>
          <textarea id="motivo" class="swal2-textarea" placeholder="Ej: Cajero ausente, emergencia, fin de turno..." style="width: 100%;"></textarea>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "🔒 Forzar Cierre",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const montoReal = document.getElementById("monto-real").value;
        const motivo = document.getElementById("motivo").value;
        if (!motivo || motivo.trim().length < 10) {
          Swal.showValidationMessage("El motivo debe tener al menos 10 caracteres");
          return false;
        }
        return { montoReal: parseFloat(montoReal) || 0, motivo };
      }
    });

    if (formValues) {
      try {
        await api.post(`/cajas/${caja.id}/forzar-cierre`, {
          monto_real: formValues.montoReal,
          observaciones: `[CIERRE FORZADO por ${usuario.nombre}] ${formValues.motivo}`
        });

        Swal.fire({
          icon: "success",
          title: "Caja Cerrada",
          text: "La caja ha sido cerrada forzadamente",
          timer: 2000,
          showConfirmButton: false
        });

        cargarDatos();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.error || "No se pudo cerrar la caja"
        });
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 👁️ VER DETALLE DE CAJA
  // ═══════════════════════════════════════════════════════════════════════════
  const verDetalleCaja = (caja) => {
    setCajaSeleccionada(caja);
    setModalDetalle(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔧 HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  const formatearMoneda = (valor) => {
    return `RD$${parseFloat(valor || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleString("es-DO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const calcularTiempoAbierta = (fechaApertura) => {
    const ahora = new Date();
    const apertura = new Date(fechaApertura);
    const diff = ahora - apertura;
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${horas}h ${minutos}m`;
  };

  // Filtrar cajas cerradas
  const cajasCerradasFiltradas = cajasCerradas.filter(caja => {
    let cumpleFiltro = true;
    
    if (filtroFecha) {
      const fechaCaja = new Date(caja.fecha_apertura).toISOString().split("T")[0];
      cumpleFiltro = cumpleFiltro && fechaCaja === filtroFecha;
    }
    
    if (filtroCajero) {
      cumpleFiltro = cumpleFiltro && 
        caja.usuario_apertura_nombre?.toLowerCase().includes(filtroCajero.toLowerCase());
    }
    
    return cumpleFiltro;
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Cargando información de cajas...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>💼 Panel de Control de Cajas</h1>
          <p style={styles.subtitle}>Supervisión en tiempo real de todas las cajas</p>
        </div>
        <button onClick={cargarDatos} style={styles.btnRefresh}>
          🔄 Actualizar
        </button>
      </div>

      {/* Resumen General */}
      <div style={styles.resumenGrid}>
        <div style={{...styles.resumenCard, borderColor: "#10b981"}}>
          <div style={styles.resumenIcon}>📊</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{resumenGeneral.totalCajasAbiertas}</span>
            <span style={styles.resumenLabel}>Cajas Abiertas</span>
          </div>
        </div>
        
        <div style={{...styles.resumenCard, borderColor: "#3b82f6"}}>
          <div style={styles.resumenIcon}>💵</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{formatearMoneda(resumenGeneral.totalEfectivo)}</span>
            <span style={styles.resumenLabel}>Total Efectivo</span>
          </div>
        </div>
        
        <div style={{...styles.resumenCard, borderColor: "#8b5cf6"}}>
          <div style={styles.resumenIcon}>💳</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{formatearMoneda(resumenGeneral.totalTarjeta)}</span>
            <span style={styles.resumenLabel}>Total Tarjeta</span>
          </div>
        </div>
        
        <div style={{...styles.resumenCard, borderColor: "#f59e0b"}}>
          <div style={styles.resumenIcon}>🏦</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{formatearMoneda(resumenGeneral.totalTransferencia)}</span>
            <span style={styles.resumenLabel}>Total Transferencia</span>
          </div>
        </div>
        
        <div style={{...styles.resumenCard, borderColor: "#06b6d4"}}>
          <div style={styles.resumenIcon}>🛒</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{resumenGeneral.numVentas}</span>
            <span style={styles.resumenLabel}>Ventas Totales</span>
          </div>
        </div>
        
        <div style={{...styles.resumenCard, borderColor: "#ec4899"}}>
          <div style={styles.resumenIcon}>💰</div>
          <div style={styles.resumenContent}>
            <span style={styles.resumenValor}>{formatearMoneda(resumenGeneral.totalVentas)}</span>
            <span style={styles.resumenLabel}>Monto Total</span>
          </div>
        </div>
      </div>

      {/* Tabs de navegación */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(vistaActiva === "abiertas" ? styles.tabActivo : {})
          }}
          onClick={() => setVistaActiva("abiertas")}
        >
          🟢 Cajas Abiertas ({cajasAbiertas.length})
        </button>
        <button
          style={{
            ...styles.tab,
            ...(vistaActiva === "cerradas" ? styles.tabActivo : {})
          }}
          onClick={() => setVistaActiva("cerradas")}
        >
          🔴 Historial Cerradas ({cajasCerradas.length})
        </button>
        <button
          style={{
            ...styles.tab,
            ...(vistaActiva === "fisicas" ? styles.tabActivo : {})
          }}
          onClick={() => setVistaActiva("fisicas")}
        >
          🖥️ Cajas Físicas ({cajasFisicas.length})
        </button>
      </div>

      {/* Contenido según vista */}
      <div style={styles.content}>
        {/* Vista: Cajas Abiertas */}
        {vistaActiva === "abiertas" && (
          <>
            {cajasAbiertas.length === 0 ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyIcon}>📭</span>
                <h3 style={styles.emptyTitle}>No hay cajas abiertas</h3>
                <p style={styles.emptyText}>Todas las cajas están cerradas en este momento</p>
              </div>
            ) : (
              <div style={styles.cajasGrid}>
                {cajasAbiertas.map((caja) => (
                  <div key={caja.id} style={styles.cajaCard}>
                    <div style={styles.cajaHeader}>
                      <div style={styles.cajaNumero}>
                        <span style={styles.cajaIcon}>🖥️</span>
                        {caja.caja_fisica_numero || caja.numero_caja}
                      </div>
                      <span style={styles.estadoAbierta}>● Abierta</span>
                    </div>
                    
                    <div style={styles.cajaCajero}>
                      <span style={styles.cajeroIcon}>👤</span>
                      <div>
                        <div style={styles.cajeroNombre}>{caja.usuario_apertura_nombre}</div>
                        <div style={styles.cajeroTiempo}>
                          ⏱️ {calcularTiempoAbierta(caja.fecha_apertura)}
                        </div>
                      </div>
                    </div>
                    
                    <div style={styles.cajaStats}>
                      <div style={styles.cajaStat}>
                        <span style={styles.statLabel}>Ventas</span>
                        <span style={styles.statValor}>{caja.num_ventas || 0}</span>
                      </div>
                      <div style={styles.cajaStat}>
                        <span style={styles.statLabel}>Total</span>
                        <span style={styles.statValor}>{formatearMoneda(caja.total_ventas)}</span>
                      </div>
                    </div>
                    
                    <div style={styles.cajaDesglose}>
                      <div style={styles.desgloseItem}>
                        <span>💵 Efectivo:</span>
                        <span>{formatearMoneda(caja.total_efectivo)}</span>
                      </div>
                      <div style={styles.desgloseItem}>
                        <span>💳 Tarjeta:</span>
                        <span>{formatearMoneda(caja.total_tarjeta)}</span>
                      </div>
                      <div style={styles.desgloseItem}>
                        <span>🏦 Transfer:</span>
                        <span>{formatearMoneda(caja.total_transferencia)}</span>
                      </div>
                    </div>
                    
                    <div style={styles.cajaAcciones}>
                      <button
                        onClick={() => verDetalleCaja(caja)}
                        style={styles.btnDetalle}
                      >
                        👁️ Ver Detalle
                      </button>
                      <button
                        onClick={() => forzarCierreCaja(caja)}
                        style={styles.btnForzarCierre}
                      >
                        🔒 Forzar Cierre
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Vista: Historial de Cajas Cerradas */}
        {vistaActiva === "cerradas" && (
          <>
            {/* Filtros */}
            <div style={styles.filtros}>
              <div style={styles.filtroGroup}>
                <label style={styles.filtroLabel}>📅 Fecha:</label>
                <input
                  type="date"
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value)}
                  style={styles.filtroInput}
                />
              </div>
              <div style={styles.filtroGroup}>
                <label style={styles.filtroLabel}>👤 Cajero:</label>
                <input
                  type="text"
                  placeholder="Buscar cajero..."
                  value={filtroCajero}
                  onChange={(e) => setFiltroCajero(e.target.value)}
                  style={styles.filtroInput}
                />
              </div>
              {(filtroFecha || filtroCajero) && (
                <button
                  onClick={() => { setFiltroFecha(""); setFiltroCajero(""); }}
                  style={styles.btnLimpiarFiltro}
                >
                  ✕ Limpiar
                </button>
              )}
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Caja</th>
                    <th style={styles.th}>Cajero</th>
                    <th style={styles.th}>Apertura</th>
                    <th style={styles.th}>Cierre</th>
                    <th style={styles.th}>Ventas</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Diferencia</th>
                    <th style={styles.th}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cajasCerradasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={styles.emptyRow}>
                        No hay cajas cerradas con los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    cajasCerradasFiltradas.slice(0, 50).map((caja) => {
                      const diferencia = parseFloat(caja.diferencia || 0);
                      return (
                        <tr key={caja.id} style={styles.tr}>
                          <td style={styles.td}>
                            <strong>{caja.caja_fisica_numero || caja.numero_caja}</strong>
                          </td>
                          <td style={styles.td}>{caja.usuario_apertura_nombre}</td>
                          <td style={styles.td}>{formatearFecha(caja.fecha_apertura)}</td>
                          <td style={styles.td}>{formatearFecha(caja.fecha_cierre)}</td>
                          <td style={{...styles.td, textAlign: "center"}}>{caja.num_ventas || 0}</td>
                          <td style={{...styles.td, fontWeight: "700"}}>{formatearMoneda(caja.total_ventas)}</td>
                          <td style={{
                            ...styles.td,
                            fontWeight: "700",
                            color: diferencia === 0 ? "#10b981" : diferencia > 0 ? "#3b82f6" : "#ef4444"
                          }}>
                            {diferencia === 0 ? "✓ Cuadre" : formatearMoneda(diferencia)}
                          </td>
                          <td style={styles.td}>
                            <button
                              onClick={() => verDetalleCaja(caja)}
                              style={styles.btnVerSmall}
                            >
                              👁️
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Vista: Cajas Físicas */}
        {vistaActiva === "fisicas" && (
          <div style={styles.cajasGrid}>
            {cajasFisicas.map((cf) => {
              const cajaAbierta = cajasAbiertas.find(c => c.caja_fisica_id === cf.id);
              return (
                <div
                  key={cf.id}
                  style={{
                    ...styles.cajaFisicaCard,
                    borderColor: cajaAbierta ? "#10b981" : "#e2e8f0"
                  }}
                >
                  <div style={styles.cfHeader}>
                    <span style={styles.cfNumero}>🖥️ {cf.numero}</span>
                    <span style={{
                      ...styles.cfEstado,
                      background: cajaAbierta ? "#d1fae5" : "#f1f5f9",
                      color: cajaAbierta ? "#065f46" : "#64748b"
                    }}>
                      {cajaAbierta ? "● En uso" : "○ Disponible"}
                    </span>
                  </div>
                  <div style={styles.cfNombre}>{cf.nombre || "Sin nombre"}</div>
                  <div style={styles.cfUbicacion}>📍 {cf.ubicacion || "Sin ubicación"}</div>
                  {cajaAbierta && (
                    <div style={styles.cfCajero}>
                      👤 {cajaAbierta.usuario_apertura_nombre}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Detalle */}
      {modalDetalle && cajaSeleccionada && (
        <div style={styles.modalOverlay} onClick={() => setModalDetalle(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                📋 Detalle de Caja
              </h2>
              <button onClick={() => setModalDetalle(false)} style={styles.btnCerrar}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.detalleSection}>
                <h3 style={styles.detalleSeccionTitulo}>Información General</h3>
                <div style={styles.detalleGrid}>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Número de Caja</span>
                    <span style={styles.detalleValor}>
                      {cajaSeleccionada.caja_fisica_numero || cajaSeleccionada.numero_caja}
                    </span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Código</span>
                    <span style={styles.detalleValor}>{cajaSeleccionada.codigo}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Cajero Apertura</span>
                    <span style={styles.detalleValor}>{cajaSeleccionada.usuario_apertura_nombre}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Estado</span>
                    <span style={{
                      ...styles.detalleValor,
                      color: cajaSeleccionada.estado === "abierta" ? "#10b981" : "#64748b"
                    }}>
                      {cajaSeleccionada.estado === "abierta" ? "🟢 Abierta" : "🔴 Cerrada"}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.detalleSection}>
                <h3 style={styles.detalleSeccionTitulo}>Fechas</h3>
                <div style={styles.detalleGrid}>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Fecha Apertura</span>
                    <span style={styles.detalleValor}>{formatearFecha(cajaSeleccionada.fecha_apertura)}</span>
                  </div>
                  {cajaSeleccionada.fecha_cierre && (
                    <div style={styles.detalleItem}>
                      <span style={styles.detalleLabel}>Fecha Cierre</span>
                      <span style={styles.detalleValor}>{formatearFecha(cajaSeleccionada.fecha_cierre)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.detalleSection}>
                <h3 style={styles.detalleSeccionTitulo}>Montos</h3>
                <div style={styles.detalleGrid}>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Monto Inicial</span>
                    <span style={styles.detalleValor}>{formatearMoneda(cajaSeleccionada.monto_inicial)}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Número de Ventas</span>
                    <span style={styles.detalleValor}>{cajaSeleccionada.num_ventas || 0}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>💵 Efectivo</span>
                    <span style={styles.detalleValor}>{formatearMoneda(cajaSeleccionada.total_efectivo)}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>💳 Tarjeta</span>
                    <span style={styles.detalleValor}>{formatearMoneda(cajaSeleccionada.total_tarjeta)}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>🏦 Transferencia</span>
                    <span style={styles.detalleValor}>{formatearMoneda(cajaSeleccionada.total_transferencia)}</span>
                  </div>
                  <div style={styles.detalleItem}>
                    <span style={styles.detalleLabel}>Total Ventas</span>
                    <span style={{...styles.detalleValor, fontSize: "20px", color: "#1e3a8a"}}>
                      {formatearMoneda(cajaSeleccionada.total_ventas)}
                    </span>
                  </div>
                </div>
              </div>

              {cajaSeleccionada.estado === "cerrada" && (
                <div style={styles.detalleSection}>
                  <h3 style={styles.detalleSeccionTitulo}>Cierre de Caja</h3>
                  <div style={styles.detalleGrid}>
                    <div style={styles.detalleItem}>
                      <span style={styles.detalleLabel}>Monto Esperado</span>
                      <span style={styles.detalleValor}>
                        {formatearMoneda(
                          parseFloat(cajaSeleccionada.monto_inicial || 0) +
                          parseFloat(cajaSeleccionada.total_efectivo || 0)
                        )}
                      </span>
                    </div>
                    <div style={styles.detalleItem}>
                      <span style={styles.detalleLabel}>Monto Real</span>
                      <span style={styles.detalleValor}>{formatearMoneda(cajaSeleccionada.monto_real)}</span>
                    </div>
                    <div style={styles.detalleItem}>
                      <span style={styles.detalleLabel}>Diferencia</span>
                      <span style={{
                        ...styles.detalleValor,
                        color: parseFloat(cajaSeleccionada.diferencia || 0) === 0 
                          ? "#10b981" 
                          : parseFloat(cajaSeleccionada.diferencia || 0) > 0 
                            ? "#3b82f6" 
                            : "#ef4444"
                      }}>
                        {parseFloat(cajaSeleccionada.diferencia || 0) === 0 
                          ? "✓ Cuadre perfecto" 
                          : formatearMoneda(cajaSeleccionada.diferencia)}
                      </span>
                    </div>
                    {cajaSeleccionada.usuario_cierre_nombre && (
                      <div style={styles.detalleItem}>
                        <span style={styles.detalleLabel}>Cerrada por</span>
                        <span style={styles.detalleValor}>{cajaSeleccionada.usuario_cierre_nombre}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(cajaSeleccionada.observaciones_apertura || cajaSeleccionada.observaciones_cierre) && (
                <div style={styles.detalleSection}>
                  <h3 style={styles.detalleSeccionTitulo}>Observaciones</h3>
                  {cajaSeleccionada.observaciones_apertura && (
                    <div style={styles.observacion}>
                      <strong>Apertura:</strong> {cajaSeleccionada.observaciones_apertura}
                    </div>
                  )}
                  {cajaSeleccionada.observaciones_cierre && (
                    <div style={styles.observacion}>
                      <strong>Cierre:</strong> {cajaSeleccionada.observaciones_cierre}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estilos de animación */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    color: "#64748b",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0",
  },
  btnRefresh: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Resumen
  resumenGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  resumenCard: {
    background: "white",
    borderRadius: "16px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    borderLeft: "4px solid",
  },
  resumenIcon: {
    fontSize: "32px",
  },
  resumenContent: {
    display: "flex",
    flexDirection: "column",
  },
  resumenValor: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
  },
  resumenLabel: {
    fontSize: "12px",
    color: "#64748b",
  },

  // Tabs
  tabs: {
    display: "flex",
    gap: "8px",
    marginBottom: "24px",
    background: "white",
    padding: "8px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  tab: {
    padding: "12px 24px",
    border: "none",
    background: "transparent",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  tabActivo: {
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
  },

  // Content
  content: {
    background: "white",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },

  // Empty State
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
  emptyIcon: {
    fontSize: "64px",
    display: "block",
    marginBottom: "16px",
    opacity: "0.3",
  },
  emptyTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#475569",
    margin: "0 0 8px 0",
  },
  emptyText: {
    fontSize: "14px",
    color: "#94a3b8",
    margin: 0,
  },

  // Cajas Grid
  cajasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "20px",
  },
  cajaCard: {
    background: "#f8fafc",
    borderRadius: "16px",
    padding: "20px",
    border: "2px solid #e2e8f0",
  },
  cajaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  cajaNumero: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },
  cajaIcon: {
    fontSize: "24px",
  },
  estadoAbierta: {
    padding: "6px 12px",
    background: "#d1fae5",
    color: "#065f46",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
  },
  cajaCajero: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    background: "white",
    borderRadius: "10px",
    marginBottom: "16px",
  },
  cajeroIcon: {
    fontSize: "24px",
  },
  cajeroNombre: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1e293b",
  },
  cajeroTiempo: {
    fontSize: "12px",
    color: "#64748b",
  },
  cajaStats: {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
  },
  cajaStat: {
    flex: 1,
    textAlign: "center",
    padding: "12px",
    background: "white",
    borderRadius: "10px",
  },
  statLabel: {
    display: "block",
    fontSize: "11px",
    color: "#64748b",
    marginBottom: "4px",
  },
  statValor: {
    display: "block",
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
  },
  cajaDesglose: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    background: "white",
    borderRadius: "10px",
    marginBottom: "16px",
  },
  desgloseItem: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    color: "#475569",
  },
  cajaAcciones: {
    display: "flex",
    gap: "8px",
  },
  btnDetalle: {
    flex: 1,
    padding: "10px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnForzarCierre: {
    flex: 1,
    padding: "10px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Filtros
  filtros: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  filtroGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filtroLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },
  filtroInput: {
    padding: "10px 14px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
  },
  btnLimpiarFiltro: {
    padding: "10px 16px",
    background: "#f1f5f9",
    color: "#64748b",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Table
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "14px 12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    borderBottom: "2px solid #e2e8f0",
    background: "#f8fafc",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    color: "#1e293b",
  },
  emptyRow: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  btnVerSmall: {
    padding: "8px 12px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },

  // Cajas Físicas
  cajaFisicaCard: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    border: "2px solid",
  },
  cfHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  cfNumero: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },
  cfEstado: {
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  cfNombre: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "4px",
  },
  cfUbicacion: {
    fontSize: "13px",
    color: "#64748b",
    marginBottom: "8px",
  },
  cfCajero: {
    fontSize: "13px",
    color: "#10b981",
    fontWeight: "600",
    padding: "8px 12px",
    background: "#d1fae5",
    borderRadius: "8px",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "white",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "700px",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #e2e8f0",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    color: "white",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "700",
    margin: 0,
  },
  btnCerrar: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    color: "white",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    fontSize: "18px",
    cursor: "pointer",
  },
  modalBody: {
    padding: "24px",
    overflowY: "auto",
  },
  detalleSection: {
    marginBottom: "24px",
  },
  detalleSeccionTitulo: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#64748b",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  detalleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  },
  detalleItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  detalleLabel: {
    fontSize: "12px",
    color: "#64748b",
  },
  detalleValor: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1e293b",
  },
  observacion: {
    padding: "12px",
    background: "#f8fafc",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#475569",
    marginBottom: "8px",
  },
};

export default GestionCajasAdmin;